import { useQuery } from '@apollo/client';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { SearchableResultsLayout } from 'components/common/layout/searchable-results';
import { FullScreenModal } from 'components/common/modals';
import { CampaignSearchMeta } from 'components/nouns/campaign/search-meta';
import { CompanySearchMeta } from 'components/nouns/company/search-meta';
import { FC, useEffect, useMemo, useState } from 'react';
import {
  Search,
  Search_searchCampaigns,
  Search_searchCompanies,
  SearchVariables,
} from 'types/apollo/Search';
import { useDebouncedEffect } from 'utils/debounce-effect.hook';
import { SearchQuery } from './search.graphql';

interface SearchProps {
  open: boolean;
  closeSearch: () => void;
  initialSearch: string;
  onSearchChange: (search: string) => void;
}

export const SearchView: FC<SearchProps> = ({
  open,
  closeSearch,
  initialSearch = '',
  onSearchChange,
}) => {
  const [search, setSearch] = useState<string>(initialSearch);
  const [debouncedSearch, setDebouncedSearch] = useState<string>(initialSearch);
  const [companies, setCompanies] = useState<Search_searchCompanies[]>([]);
  const [campaigns, setCampaigns] = useState<Search_searchCampaigns[]>([]);
  const variables = useMemo<SearchVariables>(() => {
    return { searchTerm: debouncedSearch };
  }, [debouncedSearch]);

  useDebouncedEffect(
    () => {
      setDebouncedSearch(search);
      if (search) {
        onSearchChange(search);
      }
    },
    [search],
    500,
  );

  useEffect(() => {
    if (!open) {
      setSearch('');
    }
  }, [open]);

  useQuery<Search, SearchVariables>(SearchQuery, {
    skip: !initialSearch,
    variables,
    onCompleted: (data) => {
      setCompanies(data.searchCompanies);
      setCampaigns(data.searchCampaigns);
    },
  });

  return (
    <FullScreenModal open={open} onClose={closeSearch} aria-labelledby="Search">
      <Box maxWidth="1150px" width="100%" p={2} overflow="auto">
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          marginTop={8}
          marginBottom={3}
        >
          <Typography variant="h3" component="h1">
            Search
          </Typography>
          <Box>
            <IconButton
              size="large"
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={closeSearch}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <Divider />

        <SearchableResultsLayout>
          <SearchableResultsLayout.FiltersWrapper>
            <TextField
              name="search"
              label="Search by Name"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
              }}
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </SearchableResultsLayout.FiltersWrapper>

          {debouncedSearch.length === 0 && (
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              flexDirection="row"
              paddingLeft={{ xs: '24px', md: 0 }}
            >
              <ArrowBackIcon
                sx={{
                  transform: {
                    xs: 'rotate(90deg)',
                    md: 'rotate(0deg)',
                  },
                }}
              />
              <Typography variant="h5" component="h3">
                Search across companies, campaigns, and brands
              </Typography>
            </Stack>
          )}
          {debouncedSearch.length > 0 && (
            <SearchableResultsLayout.ContentWrapper>
              <CompanySearchMeta companies={companies} />
              <CampaignSearchMeta campaigns={campaigns} />
            </SearchableResultsLayout.ContentWrapper>
          )}
        </SearchableResultsLayout>
      </Box>
    </FullScreenModal>
  );
};
